import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { EyeIcon } from "@phosphor-icons/react";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { MemoIsland } from "../../MemoIsland/MemoIsland";
import { MemoOptionsMenu } from "../../ProjectHeader/_MemoOptionsMenu";
import { ProjectInfoTooltip } from "../../ProjectHeader/_ProjectInfoTooltip";
import { ValueChainIsland } from "../../ValueChainIsland/ValueChainIsland";
import { CaseFrameIsland } from "../../FrameIsland/CaseFrameIsland";
import { ProjectSetupIsland } from "../../FrameIsland/ProjectSetupIsland";
import type { TSectionPage } from "../model/types";
import { SectionPage, sectionStickyTop } from "./SectionPage";
import { ChapterWidget } from "./ChapterWidget";
import { WidgetCard, WidgetCardHeader } from "./WidgetCard";

export function ChapterSectionScreen({
  page,
  toolsDisabled,
  toolPanel
}: {
  page: TSectionPage;
  toolsDisabled: boolean;
  toolPanel: ReactNode;
}) {
  const { activeChapter } = useChapterStore();
  const { selectedProject } = useProjectStore();
  const { isLabs } = useMeStore();
  const isTemplateLess = selectedProject?.projectTemplateSlug === "";
  const contentFormat = activeChapter?.activeMemo?.contentFormat;
  const isEditor = contentFormat !== "blocks" && contentFormat !== "html";

  return (
    <SectionPage page={page} fill={isEditor}>
      <ChapterWidget
        title={page.widgetTitle}
        titleAdornment={isLabs ? <ProjectInfoTooltip /> : undefined}
        toolsDisabled={toolsDisabled}
        menu={!isTemplateLess && isLabs ? <MemoOptionsMenu disabled={toolsDisabled} /> : undefined}
        stickyTop={sectionStickyTop(page.tabs.length > 0)}
        fill={isEditor}
        toolPanel={toolPanel}
      >
        <MemoIsland inWidget showTitle={page.widgetTitle !== activeChapter?.projectTemplateChapter.name} />
      </ChapterWidget>
    </SectionPage>
  );
}

export function ValueChainSectionScreen({ page }: { page: TSectionPage }) {
  const { t } = useTranslation("projectHome");

  return (
    <SectionPage page={page} fill>
      <WidgetCard className="min-h-0 flex-1">
        <WidgetCardHeader
          title={page.widgetTitle}
          titleAdornment={
            <span className="typo-caption flex shrink-0 items-center gap-1 rounded-full border border-foreground/10 px-2 py-0.5 text-muted-foreground">
              <EyeIcon className="size-3.5" weight="regular" />
              {t("valueChain.readOnly")}
            </span>
          }
        />
        <div className="min-h-0 flex-1 overflow-hidden rounded-b-2xl">
          <ValueChainIsland inWidget />
        </div>
      </WidgetCard>
    </SectionPage>
  );
}

export function FrameSectionScreen({ page }: { page: TSectionPage }) {
  const { viewMode } = useChapterStore();
  const isCaseFrame = viewMode === "projectFrame";
  return (
    <SectionPage page={page} fill={isCaseFrame}>
      {isCaseFrame ? <CaseFrameIsland /> : <ProjectSetupIsland />}
    </SectionPage>
  );
}

export function SectionStateScreen({ page, children }: { page: TSectionPage; children: ReactNode }) {
  return (
    <SectionPage page={page} fill>
      <WidgetCard className="min-h-0 flex-1">
        <WidgetCardHeader title={page.widgetTitle} />
        <div className="flex min-h-[60svh] flex-1 flex-col">{children}</div>
      </WidgetCard>
    </SectionPage>
  );
}
